import { Head, Link } from "@inertiajs/react"
import { ChevronRight } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { brokerPropertiesPath } from "@/routes"
import type { BreadcrumbItem, Property, Suite } from "@/types"

interface Props {
  property: Property
  suites: Suite[]
}

const statusVariant: Record<Suite["status"], "default" | "secondary" | "destructive"> = {
  available: "default",
  under_negotiation: "secondary",
  leased: "destructive",
}

const statusLabel: Record<Suite["status"], string> = {
  available: "Available",
  under_negotiation: "Under Negotiation",
  leased: "Leased",
}

export default function Show({ property, suites }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Properties", href: brokerPropertiesPath() },
    { title: property.name, href: `/broker/properties/${property.id}` },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={property.name} />

      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">{property.name}</h1>
          <p className="text-muted-foreground text-sm">
            {property.address}, {property.city}, {property.state} {property.zip_code}
          </p>
          {property.description && (
            <p className="mt-2 max-w-2xl text-sm">{property.description}</p>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {suites.map((suite) => (
            <Link key={suite.id} href={`/broker/properties/${property.id}/suites/${suite.id}`}>
              <Card className="hover:shadow-md cursor-pointer transition-shadow h-full">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base">Suite {suite.suite_number}</CardTitle>
                    <Badge variant={statusVariant[suite.status]}>
                      {statusLabel[suite.status]}
                    </Badge>
                  </div>
                  {suite.floor && (
                    <p className="text-muted-foreground text-sm">Floor {suite.floor}</p>
                  )}
                </CardHeader>
                <CardContent className="space-y-1">
                  {suite.sqft && (
                    <p className="text-sm">{Number(suite.sqft).toLocaleString()} sq ft</p>
                  )}
                  {suite.monthly_rent && (
                    <p className="text-sm font-medium">${Number(suite.monthly_rent).toLocaleString()}/mo</p>
                  )}
                  <div className="flex justify-end pt-1">
                    <ChevronRight className="text-muted-foreground h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </AppLayout>
  )
}
